/**
 * HubAgentDetailPage — HUB-AGENT-PERSONAS-V1.
 *
 * Per-persona page at /agents/:id rendering:
 *   - Identity card (avatar + display_name + role_title + bio + personality
 *     + status pill + budget gauge + pause/resume button)
 *   - Currently tracking: workflow_items rows assigned to this persona
 *   - Sessions panel: ~10 most recent sessions
 *   - Recent actions: last 30 entries from agent_audit_log
 *
 * Backed by the existing /hub/agents/:id, /hub/agents/:id/sessions,
 * /hub/agents/:id/activity endpoints (Phase 2 + 8a) plus the new
 * persona columns shipped in 00496_agent_personas.
 */
import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Card, Pill } from './components'
import { formatCurrency } from '@/utils/formatCurrency'
import {
  hubAutonomy,
  type HubAutonomyAgent,
  type HubAutonomyAgentSession,
  type HubAutonomyAgentActivity,
} from './api'
import { personaInitials, personaTone, deptLabel } from './personas'
import { posthogCapture } from './analytics'

function relTime(iso?: string): string {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

function formatDuration(start: string, end?: string): string {
  const s = new Date(start).getTime()
  const e = end ? new Date(end).getTime() : Date.now()
  const ms = e - s
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s`
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m`
  return `${(ms / 3_600_000).toFixed(1)}h`
}

export default function HubAgentDetailPage() {
  const params = useParams<{ id: string }>()
  const agentID = params.id ? decodeURIComponent(params.id) : ''
  const navigate = useNavigate()

  const [agent, setAgent] = useState<HubAutonomyAgent | null>(null)
  const [sessions, setSessions] = useState<HubAutonomyAgentSession[]>([])
  const [activity, setActivity] = useState<HubAutonomyAgentActivity[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const refresh = async () => {
    if (!agentID) return
    try {
      const [a, s, act] = await Promise.all([
        hubAutonomy.agent(agentID),
        hubAutonomy.sessions(agentID, 50),
        hubAutonomy.activity(agentID, 30),
      ])
      setAgent(a)
      setSessions(s.sessions)
      setActivity(act.activity)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agent')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
    const id = setInterval(refresh, 30_000)
    return () => clearInterval(id)
  }, [agentID])

  const onTogglePause = async () => {
    if (!agent) return
    setBusy(true)
    try {
      if (agent.paused) {
        await hubAutonomy.resume(agent.agent_id)
        posthogCapture('hub_agent_resumed', { agent_id: agent.agent_id })
      } else {
        await hubAutonomy.pause(agent.agent_id, 'paused from /agents detail page')
        posthogCapture('hub_agent_paused', { agent_id: agent.agent_id })
      }
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Toggle failed')
    } finally {
      setBusy(false)
    }
  }

  const display = agent?.display_name || agent?.agent_id || agentID
  const initials = personaInitials(display)
  const tone = personaTone(display)

  const statusLabel = agent?.paused ? 'paused' : agent?.current_status || 'unknown'

  const sessionsToday = useMemo(
    () => sessions.filter(s => new Date(s.started_at).toDateString() === new Date().toDateString()).length,
    [sessions],
  )

  const totalShips = useMemo(() => activity.filter(a => a.action === 'ship').length, [activity])

  if (loading) {
    return (
      <div style={{ padding: 14 }}>
        <div style={{ color: 'var(--text-dim)' }}>Loading agent…</div>
      </div>
    )
  }

  if (error || !agent) {
    return (
      <div style={{ padding: 14 }}>
        <div style={{ marginBottom: 10 }}>
          <Link to="/agents" style={{ color: 'var(--text-dim)', fontSize: 12 }}>← all agents</Link>
        </div>
        <Card>
          <div style={{ padding: 14, color: 'var(--err, #f88)' }}>
            {error || 'Agent not found'}
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div>
        <Link to="/agents" style={{ color: 'var(--text-dim)', fontSize: 12 }}>← all agents</Link>
      </div>

      {/* Identity card */}
      <Card>
        <div style={{ padding: 16, display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: tone, color: '#0c0c0e',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, fontWeight: 700, flexShrink: 0,
          }}>{initials}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: 22 }}>{display}</h1>
              <Pill variant={agent.paused ? 'warn' : agent.current_status === 'errored' ? 'err' : agent.current_status === 'running' ? 'live' : 'info'}>
                {statusLabel}
              </Pill>
              {agent.product && (
                <Pill variant={agent.product === 'tenon' ? 'info' : agent.product === 'benchpoint' ? 'live' : 'ghost'}>
                  {agent.product}
                </Pill>
              )}
              <span style={{ color: 'var(--text-dim)', fontSize: 12 }}>{deptLabel(agent.department)}</span>
            </div>
            {agent.role_title && (
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>{agent.role_title}</div>
            )}
            {agent.bio && (
              <p style={{ margin: '6px 0 0', lineHeight: 1.5, color: 'var(--text)' }}>{agent.bio}</p>
            )}
            {agent.personality && (
              <p style={{ margin: '4px 0 0', fontStyle: 'italic', color: 'var(--text-muted)', fontSize: 13 }}>{agent.personality}</p>
            )}
            <div style={{ fontSize: 11, color: 'var(--text-dim)', fontFamily: 'monospace', marginTop: 4 }}>
              {agent.agent_id}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-end' }}>
            <button
              type="button"
              onClick={onTogglePause}
              disabled={busy}
              style={{
                padding: '6px 14px', borderRadius: 6,
                border: '1px solid var(--border, rgba(255,255,255,0.15))',
                background: agent.paused ? 'var(--accent, #4ade80)' : 'transparent',
                color: agent.paused ? '#000' : 'var(--text)',
                cursor: busy ? 'wait' : 'pointer',
                fontSize: 13,
              }}
            >
              {busy ? '…' : agent.paused ? 'Resume' : 'Pause'}
            </button>
          </div>
        </div>
        <div style={{
          padding: '10px 16px',
          borderTop: '1px solid var(--border, rgba(255,255,255,0.05))',
          display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12,
          fontSize: 12,
        }}>
          <Stat label="Sessions today" value={`${sessionsToday}`} />
          <Stat label="Sessions (50 most recent)" value={`${sessions.length}`} />
          <Stat label="24h actions" value={`${agent.action_count_24h}`} />
          <Stat label="Budget cycle" value={`${formatCurrency(agent.budget_usd_spent, { decimals: 2 })} / ${agent.budget_tokens_spent.toLocaleString()} tok`} />
        </div>
      </Card>

      {/* Recent ships (only meaningful for spawn-coder) */}
      {totalShips > 0 && (
        <Card>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border, rgba(255,255,255,0.05))', fontSize: 11, color: 'var(--text-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Recent ships ({totalShips})
          </div>
          <div style={{ padding: 8 }}>
            {activity.filter(a => a.action === 'ship').slice(0, 10).map(a => (
              <div key={a.id} style={{ padding: '6px 10px', display: 'flex', gap: 8, fontSize: 12, alignItems: 'baseline' }}>
                <span style={{ color: 'var(--text-dim)', fontFamily: 'monospace', minWidth: 90 }}>{relTime(a.created_at)}</span>
                <span style={{ flex: 1 }}>
                  shipped <code style={{ color: 'var(--text)' }}>{a.target_id || '—'}</code>
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Sessions panel */}
      <Card>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border, rgba(255,255,255,0.05))', fontSize: 11, color: 'var(--text-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Sessions ({sessions.length})
        </div>
        {sessions.length === 0 ? (
          <div style={{ padding: 14, color: 'var(--text-dim)', fontSize: 13 }}>No sessions yet.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border, rgba(255,255,255,0.06))' }}>
                  <th style={th}>Started</th>
                  <th style={th}>Duration</th>
                  <th style={th}>Status</th>
                  <th style={th}>Actions</th>
                  <th style={th}>Session ID</th>
                </tr>
              </thead>
              <tbody>
                {sessions.slice(0, 30).map(s => (
                  <tr key={s.id} style={{ borderBottom: '1px solid var(--border, rgba(255,255,255,0.03))' }}>
                    <td style={td}>{relTime(s.started_at)}</td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{formatDuration(s.started_at, s.ended_at)}</td>
                    <td style={td}>
                      <Pill variant={s.status === 'running' ? 'live' : s.status === 'errored' ? 'err' : s.status === 'paused' ? 'warn' : 'ghost'}>
                        {s.status}
                      </Pill>
                    </td>
                    <td style={{ ...td, fontFamily: 'monospace' }}>{s.action_count}</td>
                    <td style={{ ...td, fontFamily: 'monospace', color: 'var(--text-muted)', fontSize: 11 }}>
                      {s.active_row_id ? (
                        <span title={`Claimed ${s.active_claimed_at ? relTime(s.active_claimed_at) : 'unknown'}`}
                          style={{ color: 'var(--brand-gold, #c9a227)', fontWeight: 600 }}>
                          {s.active_row_id}
                          {s.active_heartbeat_at && (() => {
                            const ageMin = (Date.now() - new Date(s.active_heartbeat_at).getTime()) / 60_000
                            const c = ageMin < 5 ? '#22c55e' : ageMin < 15 ? '#f59e0b' : '#ef4444'
                            return <span style={{ marginLeft: 4, width: 6, height: 6, borderRadius: '50%', background: c, display: 'inline-block', verticalAlign: 'middle' }} title={`Heartbeat ${Math.round(ageMin)}m ago`} />
                          })()}
                        </span>
                      ) : (
                        <span>{s.id.slice(0, 8)}…</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Recent actions */}
      <Card>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border, rgba(255,255,255,0.05))', fontSize: 11, color: 'var(--text-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Recent actions
        </div>
        {activity.length === 0 ? (
          <div style={{ padding: 14, color: 'var(--text-dim)', fontSize: 13 }}>No actions logged.</div>
        ) : (
          <div style={{ padding: 6 }}>
            {activity.slice(0, 30).map(a => (
              <div key={a.id} style={{
                padding: '6px 10px',
                display: 'flex', gap: 10, alignItems: 'baseline',
                fontSize: 12,
              }}>
                <span style={{ color: 'var(--text-dim)', fontFamily: 'monospace', minWidth: 90 }}>{relTime(a.created_at)}</span>
                <span style={{ fontFamily: 'monospace', color: 'var(--text)', minWidth: 130 }}>{a.action}</span>
                <span style={{ flex: 1, color: 'var(--text-muted)' }}>
                  {a.target_table && (
                    <>
                      {a.target_table}
                      {a.target_id && <code style={{ marginLeft: 6, color: 'var(--text)' }}>{a.target_id}</code>}
                    </>
                  )}
                </span>
                {a.reviewer_decision && (
                  <Pill variant={a.reviewer_decision === 'approve' ? 'live' : 'warn'}>
                    {a.reviewer_decision}
                  </Pill>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ color: 'var(--text-dim)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
      <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{value}</span>
    </div>
  )
}

const th: React.CSSProperties = { padding: '8px 10px', fontSize: 11, color: 'var(--text-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }
const td: React.CSSProperties = { padding: '8px 10px', verticalAlign: 'middle' }
